import { Grid, GridAreas, StyledFlex, StyledMargin } from '@dreryk/react-components';

import {
   AFTER_ISSUED_FROM_WIDTH,
   DOSAGE_TO_QUANTITY_MIN_GAP,
   ISSUED_FROM_TO_AFTER_ISSUED_FROM_GAP,
   QUANTITY_TO_TREATMENT_DAYS_GAP,
   QUANTITY_WIDTH,
} from '../../prescriptionGrid.constants';

type DraftStandardPrescriptionGridProps = {
   readonly dosage: JSX.Element;
   readonly issuedFrom: JSX.Element;
   readonly quantity: JSX.Element;
   readonly issuedTo: JSX.Element;
   readonly activeIngredients: JSX.Element;
   readonly additionalEntitlements: JSX.Element;
   readonly additionalRequirements: JSX.Element;
   readonly numericalDosage: JSX.Element | undefined;
   readonly treatmentDaysCount: JSX.Element;
   readonly treatmentDaysCountExceeded: JSX.Element | undefined;
   readonly dosageExceedingPackages: JSX.Element | undefined;
   readonly dosageFrequencyNotAllowed: JSX.Element | undefined;
   readonly medicineSufficientFor: JSX.Element | undefined;
};

type AREA =
   | 'dosageRow'
   | 'treatmentDaysCountExceeded'
   | 'dosageExceedingPackages'
   | 'dosageFrequencyNotAllowed'
   | 'dosage'
   | 'issuedFrom'
   | 'additionalEntitlements'
   | 'additionalRequirements';

type AreasOptions = {
   dosageFrequencyNotAllowedPresent: boolean;
   treatmentDaysCountExceededPresent: boolean;
   dosageExceedingPackagesPresent: boolean;
};

const SMALL_COLUMNS_WIDTH = 'minmax(0, 1fr)';

const MESSAGE_ROWS = {
   dosageExceedingPackages: 'dosageExceedingPackages dosageExceedingPackages dosageExceedingPackages',
   dosageFrequencyNotAllowed: 'dosageFrequencyNotAllowed dosageFrequencyNotAllowed dosageFrequencyNotAllowed',
   treatmentDaysCountExceeded: 'treatmentDaysCountExceeded treatmentDaysCountExceeded treatmentDaysCountExceeded',
} as const;

type MessageRow = typeof MESSAGE_ROWS[keyof typeof MESSAGE_ROWS];

const getMessageRows = (options: AreasOptions): MessageRow[] => {
   const rows: MessageRow[] = [];

   if (options.dosageFrequencyNotAllowedPresent) {
      rows.push(MESSAGE_ROWS.dosageFrequencyNotAllowed);
   }

   if (options.treatmentDaysCountExceededPresent) {
      rows.push(MESSAGE_ROWS.treatmentDaysCountExceeded);
   }

   if (options.dosageExceedingPackagesPresent) {
      rows.push(MESSAGE_ROWS.dosageExceedingPackages);
   }

   return rows;
};

const getAreas = (options: AreasOptions): GridAreas<AREA> => {
   return {
      small: {
         template: [
            'dosageRow dosageRow dosageRow',
            ...getMessageRows(options),
            'dosage dosage dosage',
            'issuedFrom issuedFrom issuedFrom',
            'additionalEntitlements additionalRequirements additionalRequirements',
         ],
         columnsWidth: SMALL_COLUMNS_WIDTH,
      },
   };
};

export const DraftStandardPrescriptionGrid = (props: DraftStandardPrescriptionGridProps): JSX.Element => {
   const {
      dosage,
      issuedFrom,
      quantity,
      issuedTo,
      activeIngredients,
      additionalEntitlements,
      additionalRequirements,
      numericalDosage,
      treatmentDaysCount,
      treatmentDaysCountExceeded,
      dosageExceedingPackages,
      dosageFrequencyNotAllowed,
      medicineSufficientFor,
   } = props;
   return (
      <Grid<AREA>
         areas={getAreas({
            dosageFrequencyNotAllowedPresent: Boolean(dosageFrequencyNotAllowed),
            treatmentDaysCountExceededPresent: Boolean(treatmentDaysCountExceeded),
            dosageExceedingPackagesPresent: Boolean(dosageExceedingPackages),
         })}
         testId={'draftStandardPrescription'}
         compact
         dosageRow={
            <StyledFlex
               direction={'row'}
               gap={DOSAGE_TO_QUANTITY_MIN_GAP}
               justifyContent={'space-between'}
               alignItems={'flex-start'}
               flexWrap={'wrap'}
            >
               {numericalDosage}
               <StyledFlex direction={'row'} gap={QUANTITY_TO_TREATMENT_DAYS_GAP} alignItems={'flex-start'}>
                  <StyledFlex flex={`0 0 ${QUANTITY_WIDTH}`}>{quantity}</StyledFlex>
                  {treatmentDaysCount}
               </StyledFlex>
            </StyledFlex>
         }
         treatmentDaysCountExceeded={treatmentDaysCountExceeded}
         dosageExceedingPackages={dosageExceedingPackages}
         dosageFrequencyNotAllowed={dosageFrequencyNotAllowed}
         dosage={dosage}
         issuedFrom={
            <StyledFlex direction={'row'} gap={ISSUED_FROM_TO_AFTER_ISSUED_FROM_GAP}>
               <StyledFlex flex={'1'}>{issuedFrom}</StyledFlex>
               <StyledFlex
                  direction={'column'}
                  gap={'2px'}
                  alignItems={'center'}
                  flex={`${AFTER_ISSUED_FROM_WIDTH} 0 0`}
               >
                  <StyledMargin top={12}>
                     {issuedTo}
                     {medicineSufficientFor}
                     {activeIngredients}
                  </StyledMargin>
               </StyledFlex>
            </StyledFlex>
         }
         additionalEntitlements={additionalEntitlements}
         additionalRequirements={additionalRequirements}
      />
   );
};
